import { RoleContext } from "#src/routes/settings/roles-and-permissions/roles/[roleId]/detail/RoleContext";
import { useQuery } from "@tanstack/react-query";
import {
  CheckboxInput,
  FilterBuilder,
  FilterBuilderFieldsType,
  Icon,
  Panel,
  TextAreaInput,
  TextInput,
  Tooltip,
  useFormContext,
} from "@validereinc/common-components";
import { RoleUpdateSchema, RolesAdapter } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useContext } from "react";
import { useDebounce } from "use-debounce";
import styles from "./EditRoleFormPanel.module.css";
import useLocalization from "#src/hooks/useLocalization";

const cx = classNames.bind(styles);

export const EditRoleFormPanel = ({
  fields,
  filter,
  setFilter,
}: {
  filter: object;
  setFilter: React.Dispatch<React.SetStateAction<object>>;
  fields: FilterBuilderFieldsType;
}) => {
  const { localize } = useLocalization();
  const form = useFormContext();
  const { roleQuery } = useContext(RoleContext) ?? {};
  const [enteredRoleName] = useDebounce<string>(
    form.watch(RoleUpdateSchema.keyof().Enum.name) ?? "",
    500
  );

  const isEditedNameSameAsOriginalName =
    enteredRoleName.trim().toLocaleLowerCase() ===
    roleQuery?.data?.name.trim().toLocaleLowerCase();
  const duplicateQueryPayload: Parameters<typeof RolesAdapter.getList>[0] = {
    filters: {
      name: enteredRoleName,
      // @ts-expect-error TODO: fix filter typing
      isAlreadyFormatted: true,
    },
  };

  const { data: duplicateRoles } = useQuery({
    queryKey: ["roles", duplicateQueryPayload],
    queryFn: () => RolesAdapter.getList(duplicateQueryPayload),
    enabled: Boolean(enteredRoleName && !isEditedNameSameAsOriginalName),
  });

  const hasFacilitiesReadFilter = form.watch("hasFacilitiesReadFilter");

  const localizedLowerCasedFacility = localize("facility").toLowerCase();
  const localizedLowerCasedEquipment = localize("equipment").toLowerCase();
  return (
    <Panel
      title="Details"
      className={cx("container")}
    >
      <TextInput
        name="name"
        label="Name"
        isRequired
        rules={{
          validate: {
            uniqueName: () => {
              return !isEditedNameSameAsOriginalName &&
                duplicateRoles?.total_entries
                ? "This role already exists"
                : true;
            },
          },
        }}
      />
      <TextAreaInput
        name="description"
        label="Description"
      />
      <div className={cx("checkbox-container")}>
        <CheckboxInput
          name="hasFacilitiesReadFilter"
          label={`Apply ${localizedLowerCasedFacility} filters to role`}
          isLabelShown={false}
          isOptionalTextShown={false}
          className={cx("checkbox")}
        />
        <Tooltip
          content={`Applying ${localizedLowerCasedFacility} filters will allow you to set specific filtering criteria on this role based off ${localizedLowerCasedFacility}. This will cascade to all resources associated to the ${localizedLowerCasedFacility}, such as ${localizedLowerCasedEquipment} and forms on the ${localizedLowerCasedFacility}.`}
          contentClassName={cx("tooltip-container")}
        >
          <Icon
            className={cx("tooltip-icon")}
            variant="info"
            size={16}
          />
        </Tooltip>
      </div>
      {hasFacilitiesReadFilter ? (
        <div className={cx("facility-filter-details-section")}>
          <p className={cx("heading")}>Filter Details</p>
          <FilterBuilder
            className={cx("filter-builder")}
            fields={fields}
            filter={filter}
            setFilter={setFilter}
            maxDepth={1}
          />
        </div>
      ) : null}
    </Panel>
  );
};
